import { CompanyTypeSlug } from './companies'

export const WORKFLOW_STATUS = {
  DRAFT: 'draft',
  CONTACTS_REQUESTED: 'contacts_requested',
  READY_TO_LAUNCH: 'ready_to_launch',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  PAUSED: 'paused',
} as const

export type WorkflowStatus =
  (typeof WORKFLOW_STATUS)[keyof typeof WORKFLOW_STATUS]

// Statuses where the campaign's email sequence has launched (or is queued
// to launch). Removing companies or contacts in these states alters a live
// sequence, so destructive actions must be confirmed (FIR-7549).
export const LIVE_SEQUENCE_STATUSES: readonly WorkflowStatus[] = [
  WORKFLOW_STATUS.SCHEDULED,
  WORKFLOW_STATUS.ACTIVE,
  WORKFLOW_STATUS.PAUSED,
]

export function isSequenceLive(
  status: WorkflowStatus | null | undefined
): boolean {
  return !!status && LIVE_SEQUENCE_STATUSES.includes(status)
}

export const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  [WORKFLOW_STATUS.DRAFT]: 'Draft',
  [WORKFLOW_STATUS.CONTACTS_REQUESTED]: 'Contacts Requested',
  [WORKFLOW_STATUS.READY_TO_LAUNCH]: 'Ready to Launch',
  [WORKFLOW_STATUS.SCHEDULED]: 'Scheduled',
  [WORKFLOW_STATUS.ACTIVE]: 'Active',
  [WORKFLOW_STATUS.COMPLETE]: 'Complete',
  [WORKFLOW_STATUS.PAUSED]: 'Paused',
}

export type Campaign = {
  id: number
  company_id: number
  color: string

  name: string
  description: string
  type: {
    id: number
    name: string
    slug: CompanyTypeSlug
  } | null
  product?: {
    id: number
    name: string
  }

  created: string
  modified: string
  days_since_last_update: string

  days_since_sent: number | null
  days_since_reply: number | null

  contact_company_count: number
  door_count: number

  deal_count: number
  deal_perc: number
  deal_perc_won: number

  contact_count: number
  companies_with_contacts: number
  companies_with_contacts_perc: number
  taro: number
  taro_closed: number
  taro_est_meals_day: number

  workflow_status: WorkflowStatus
  launch_date: string | null
  actual_launch_date: string | null
  scheduled_launch_at: string | null
  auto_launch_failure_reason: string | null
  auto_launch_attempted_at: string | null
  owner_id: number | null
  owner_name: string | null
  // Users who receive this campaign's notification emails (e.g. interested
  // replies). Empty means the default: the campaign owner.
  notification_recipient_ids: number[]

  bison_campaign_id: number | null
  interested_rate: number | null
  reply_count: number | null
  unread_reply_count: number | null
  action_needed_count: number | null
  bounce_count: number | null
  is_archived: boolean
}
